import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Table,
  Badge,
  Spinner,
  Alert,
} from "reactstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import PageContainer from "components/ui/PageContainer";
import useCurrentUser from "hooks/useCurrentUser";
import useAxiosPrivate from "hooks/useAxiosPrivate";

const currency = (value) =>
  new Intl.NumberFormat("en-TN", {
    style: "currency",
    currency: "TND",
    minimumFractionDigits: 3,
  }).format(Number(value || 0));

const statusColor = {
  OPEN: "success",
  CLOSED: "secondary",
  FORCE_CLOSED: "danger",
};

const StatCard = ({ icon, label, value, color = "primary", sub }) => (
  <Col lg="3" md="6" className="mb-4">
    <Card className="shadow h-100">
      <CardBody>
        <Row>
          <Col>
            <p className="text-uppercase text-muted mb-1 small font-weight-bold">
              {label}
            </p>
            <span className="h3 font-weight-bold mb-0">{value}</span>
            {sub && <p className="text-sm text-muted mb-0 mt-1">{sub}</p>}
          </Col>
          <Col className="col-auto">
            <div
              className={`icon icon-shape bg-${color} text-white rounded-circle shadow`}
            >
              <i className={icon} />
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  </Col>
);

const CashierDashboard = ({ cashierId: cashierIdProp }) => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser() || {};
  const cashierId = cashierIdProp || user?.id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!cashierId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosPrivate.get(
        `/api/tenant/cashier-dashboard/${cashierId}`
      );
      setData(res.data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to load the cashier dashboard."
      );
    } finally {
      setLoading(false);
    }
  }, [cashierId, axiosPrivate]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-6">
          <Spinner color="primary" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Alert color="danger">{error}</Alert>
      </PageContainer>
    );
  }

  if (!data) return null;

  const {
    totalSessions,
    openSessions,
    closedSessions,
    forceClosedSessions,
    totalSales,
    totalTicketsCount,
    averageTicketAmount,
    totalWithdrawalsApproved,
    pendingWithdrawalsCount,
    pendingWithdrawalsAmount,
    totalCashDifference,
    sessionsWithDiscrepancy,
    paymentMethodBreakdown,
    recentSessions,
  } = data;

  const chartData = paymentMethodBreakdown.map((p) => ({
    method: p.paymentMethod,
    total: Number(p.totalAmount),
    count: p.transactionCount,
  }));

  return (
    <PageContainer>
      <Container fluid>
        {/* KPI row */}
        <Row>
          <StatCard
            icon="ni ni-money-coins"
            label="Total Sales"
            value={currency(totalSales)}
            color="success"
            sub={`${totalTicketsCount} tickets`}
          />
          <StatCard
            icon="ni ni-chart-bar-32"
            label="Average Ticket"
            value={currency(averageTicketAmount)}
            color="primary"
          />
          <StatCard
            icon="ni ni-money-coins"
            label="Approved Withdrawals"
            value={currency(totalWithdrawalsApproved)}
            color="warning"
            sub={
              pendingWithdrawalsCount > 0
                ? `${pendingWithdrawalsCount} pending (${currency(
                    pendingWithdrawalsAmount
                  )})`
                : "None pending"
            }
          />
          <StatCard
            icon="ni ni-settings-gear-65"
            label="Total Cash Difference"
            value={currency(totalCashDifference)}
            color={Number(totalCashDifference) === 0 ? "success" : "danger"}
            sub={`${sessionsWithDiscrepancy} session(s) with discrepancy`}
          />
        </Row>

        {/* Sessions overview */}
        <Row>
          <StatCard
            icon="ni ni-single-copy-04"
            label="Total Sessions"
            value={totalSessions}
            color="info"
          />
          <StatCard
            icon="ni ni-active-40"
            label="Open Sessions"
            value={openSessions}
            color="success"
          />
          <StatCard
            icon="ni ni-fat-remove"
            label="Closed Sessions"
            value={closedSessions}
            color="secondary"
          />
          <StatCard
            icon="ni ni-button-power"
            label="Force-Closed"
            value={forceClosedSessions}
            color="danger"
          />
        </Row>

        {/* Payment method breakdown */}
        <Row>
          <Col lg="12" className="mb-4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Breakdown by Payment Method</h3>
              </CardHeader>
              <CardBody>
                {chartData.length === 0 ? (
                  <p className="text-muted mb-0">No data available.</p>
                ) : (
                  <div style={{ width: "100%", height: 300 }}>
                    <ResponsiveContainer>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="method" />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) =>
                            name === "total" ? currency(value) : value
                          }
                        />
                        <Bar dataKey="total" fill="#5e72e4" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Recent sessions table */}
        <Row>
          <Col lg="12" className="mb-4">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">Recent Sessions</h3>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th>Opened</th>
                    <th>Closed</th>
                    <th>Status</th>
                    <th>Sales</th>
                    <th>Tickets</th>
                    <th>Withdrawals</th>
                    <th>Opening Balance</th>
                    <th>Counted Balance</th>
                    <th>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {recentSessions.length === 0 && (
                    <tr>
                      <td colSpan="9" className="text-center text-muted">
                        No sessions found.
                      </td>
                    </tr>
                  )}
                  {recentSessions.map((s) => (
                    <tr key={s.sessionId}>
                      <td>{new Date(s.openingDate).toLocaleString("en-US")}</td>
                      <td>
                        {s.closingDate
                          ? new Date(s.closingDate).toLocaleString("en-US")
                          : "—"}
                      </td>
                      <td>
                        <Badge color={statusColor[s.status] || "secondary"} pill>
                          {s.status}
                          {s.autoClosed && " (auto)"}
                        </Badge>
                      </td>
                      <td>{currency(s.totalSales)}</td>
                      <td>{s.ticketsCount}</td>
                      <td>{currency(s.totalWithdrawals)}</td>
                      <td>{currency(s.openingBalance)}</td>
                      <td>{s.closingBalance != null ? currency(s.closingBalance) : "—"}</td>
                      <td>
                        <span
                          className={
                            s.difference == null || Number(s.difference) === 0
                              ? "text-success"
                              : "text-danger font-weight-bold"
                          }
                        >
                          {s.difference != null ? currency(s.difference) : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>
    </PageContainer>
  );
};

export default CashierDashboard;